import DOMPurify from "dompurify";
import { API_HOST } from "@/utils/media";

const isAbsoluteUrl = url => /^https?:\/\//.test(url) || url.startsWith("//");

/**
 * The backend renders media (img src, file/download links, ...) as root-relative
 * URLs (e.g. "/api/media/file/altar.webp"), which only resolve against the CMS host,
 * not this frontend's own origin. Rewrite them to absolute URLs before rendering.
 * @param {*} html HTML string to resolve
 * @returns {string} HTML string with root-relative URLs made absolute
 */
function resolveMediaUrls(html) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("img[src], source[src]").forEach(el => {
    const src = el.getAttribute("src");
    if (src && src.startsWith("/") && !isAbsoluteUrl(src)) {
      el.setAttribute("src", `${API_HOST}${src}`);
    }
  });
  doc.querySelectorAll("a[href]").forEach(el => {
    const href = el.getAttribute("href");
    if (href && href.startsWith("/") && !isAbsoluteUrl(href)) {
      el.setAttribute("href", `${API_HOST}${href}`);
    }
  });
  return doc.body.innerHTML;
}

/**
 * Sanitizes HTML content to prevent XSS attacks.
 * @param {*} html HTML string to sanitize
 * @returns {string} Sanitized HTML string
 */
export function sanitizeHtml(html) {
  if (!html) return "";
  return DOMPurify.sanitize(resolveMediaUrls(html), { ADD_ATTR: ["target"] });
}

/**
 * Converts HTML to plain text and truncates it if necessary.
 * @param {*} html HTML string to convert
 * @param {number} maxLength Maximum length of the resulting plain text
 * @returns {string} Plain text string
 */
export function htmlToPlainText(html, maxLength = 150) {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc
    .querySelectorAll("p, li, h1, h2, h3, h4, h5, h6, br")
    .forEach(el => el.after(" "));
  const text = (doc.body.textContent || "").trim().replace(/\s+/g, " ");
  return text.length > maxLength
    ? text.slice(0, maxLength).trimEnd() + "…"
    : text;
}
